import classNames from "classnames";
import React from "react";
import useTranslations from "../../hooks/useTranslations";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import { isNullOrWhiteSpace } from "../../shared/string";
import { formatPhoneNumberLink } from "../../utilities/links";
import { PortableTextColorThemeProps } from "../PortableText/PortableText";
import SanityImage from "../SanityImage";
import SanityLink from "../SanityLink";

export interface NearbyOfficeListProps {
  title?: string;
  subtitle?: string;
  offices?: [
    {
      _id?: string;
      thumbnail?: SanityImageSourceExt;
      title?: string;
      contact?: {
        address: string;
        officeHours: [
          {
            days: string;
            openingHours: string;
          }
        ];
        phoneNumber: string;
      };
    }
  ];
  portableTextColorThemeProps?: PortableTextColorThemeProps;
}

const NearbyOfficeList = ({
  title,
  subtitle,
  offices,
  portableTextColorThemeProps
}: NearbyOfficeListProps) => {
  const translations = useTranslations();
  const {
    elementClasses: { h3Subheader: subtitleClasses = "text-subheader-primary" } = {}
  } = portableTextColorThemeProps || {};
  return (
    <aside>
      {!isNullOrWhiteSpace(title) && <h2 className="text-current">{title}</h2>}
      {!isNullOrWhiteSpace(subtitle) && (
        <h3
          className={`${subtitleClasses} text-base uppercase font-bold lg:text-xl lg:mt-2`}
        >
          {subtitle}
        </h3>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
        {offices?.map(office => (
          <SanityLink
            reference={office}
            className={classNames(
              "no-underline card-basic group flex-col flex-1 min-w-card-small items-center p-8 cursor-pointer h-full",
              "hover:border-brand-primary-70 hover:shadow-card-lifted hover:-translate-y-3",
              "focus:outline-none focus:shadow-card-outline"
            )}
            key={office._id}
          >
            <div>
              {office.thumbnail && office.title ? (
                <div className="relative mb-7">
                  <SanityImage
                    className="rounded-md w-full object-cover mb-2 lg:mb-0"
                    image={office.thumbnail}
                    loading="lazy"
                    width={150}
                    height={90}
                  />
                  <h4 className="absolute text-neutral-100 z-20 text-center text-2xl font-bold sm:font-normal top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2 leading-8">
                    {office.title}
                  </h4>
                  <div className="absolute w-full h-full inset-0 m-0 bg-opacity-50 bg-black rounded-md"></div>
                </div>
              ) : (
                <h4 className="text-center leading-8 mb-8 text-2xl font-bold">
                  {office.title}
                </h4>
              )}
              {!!office.contact && !isNullOrWhiteSpace(office.contact.address) && (
                <div>
                  <h4 className="text-contact-info-h4 font-bold uppercase text-base">
                    {translations.address}
                  </h4>
                  <p className="text-lg mb-6">{office.contact.address}</p>
                </div>
              )}
              {!!office.contact && !isNullOrWhiteSpace(office.contact.phoneNumber) && (
                <div>
                  <h4 className="text-contact-info-h4 font-bold uppercase text-base">
                    {translations.phoneNumber}
                  </h4>
                  <p className="mb-6">
                    <a
                      href={formatPhoneNumberLink(office.contact.phoneNumber)}
                      className="text-lg text-current no-underline hover:opacity-90 hover:underline focus:outline-none focus:shadow-outline-primary"
                    >
                      {office.contact.phoneNumber}
                    </a>
                  </p>
                </div>
              )}
              {office.contact?.officeHours && office.contact?.officeHours.length > 0 && (
                <div>
                  <h4 className="text-contact-info-h4 font-bold uppercase text-base">
                    {translations.officeHours}
                  </h4>
                  {office.contact.officeHours && (
                    <ul className="text-paragraph-primary text-lg">
                      {office.contact.officeHours.map(officeHour => (
                        <li key={officeHour.days} className="flex justify-between">
                          {!isNullOrWhiteSpace(officeHour.days) && (
                            <div>{officeHour.days}</div>
                          )}
                          {!isNullOrWhiteSpace(officeHour.openingHours) && (
                            <div className="font-bold">{officeHour.openingHours}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </SanityLink>
        ))}
      </div>
    </aside>
  );
};
export default NearbyOfficeList;
